import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, curSym, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UploadSimple, MagicWand, LinkSimple, DownloadSimple, PencilSimple } from "@phosphor-icons/react";
import { CurrencySelect } from "@finance/components/CurrencySelect";

function ym(d) { return d.toISOString().slice(0, 7); }

export default function BankRec() {
  const [banks, setBanks] = useState([]);
  const [active, setActive] = useState(null);
  const [month, setMonth] = useState(ym(new Date()));
  const [lines, setLines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [recon, setRecon] = useState(null);
  const [newBankOpen, setNewBankOpen] = useState(false);
  const [bankForm, setBankForm] = useState({ currency: "TZS" });
  const [editId, setEditId] = useState(null);

  const loadBanks = useCallback(() => api.get("/bank-accounts").then((r) => { setBanks(r.data); if (!active && r.data[0]) setActive(r.data[0].id); }), [active]);
  useEffect(() => { loadBanks(); }, [loadBanks]);

  const loadLines = useCallback(() => {
    if (!active) return;
    api.get(`/bank-accounts/${active}/lines`, { params: { month } }).then((r) => setLines(r.data));
    api.get("/payments", { params: { unreconciled: true } }).then((r) => setPayments(r.data));
    api.get(`/bank-accounts/${active}/reconciliation`, { params: { month } }).then((r) => setRecon(r.data)).catch(() => setRecon(null));
  }, [active, month]);
  useEffect(() => { loadLines(); }, [loadLines]);

  const createBank = async () => {
    if (!bankForm.bank_name) { toast.error("Bank name required"); return; }
    try {
      const { data } = await api.post("/bank-accounts", bankForm);
      toast.success("Bank account added");
      setNewBankOpen(false); setBankForm({ currency: "TZS" });
      setBanks((b) => [...b, data]); setActive(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openNewBank = () => { setEditId(null); setBankForm({ currency: "TZS" }); setNewBankOpen(true); };

  const openEditBank = () => {
    const b = banks.find((x) => x.id === active);
    if (!b) return;
    setEditId(b.id);
    setBankForm({
      bank_name: b.raw_bank_name ?? b.bank_name ?? "",
      currency: b.currency || "TZS",
      account_number: b.account_number || "",
      account_name: b.account_name || "",
      swift_code: b.swift_code || "",
      branch: b.branch || "",
      is_cash: !!b.is_cash,
    });
    setNewBankOpen(true);
  };

  const saveBank = async () => {
    if (!bankForm.bank_name) { toast.error("Bank name required"); return; }
    if (!editId) return createBank();
    try {
      await api.put(`/bank-accounts/${editId}`, bankForm);
      toast.success("Bank account updated");
      setNewBankOpen(false); setEditId(null); setBankForm({ currency: "TZS" });
      loadBanks();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadCsv = async (file) => {
    if (!file || !active) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/bank-accounts/${active}/statement/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = data.skipped ? `${data.inserted} new lines imported, ${data.skipped} duplicate lines skipped` : `${data.inserted} statement lines imported`;
      toast.success(msg); loadLines();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadTemplate = () => {
    const csv = [
      "date,payee,details,credit,debit,balance",
      "2026-06-01,ACME Ltd,Invoice INV-1001 receipt,1250000.00,,5250000.00",
      "2026-06-03,TANESCO,Electricity bill,,320000.00,4930000.00",
      "2026-06-05,Cash withdrawal,ATM Main St,,200000.00,4730000.00",
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bank-statement-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const autoMatch = async () => {
    try {
      const { data } = await api.post(`/bank-accounts/${active}/auto-match`, {}, { params: { month } });
      toast.success(`${data.matched} lines auto-matched`); loadLines();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assignUnassigned = async () => {
    if (!active) return;
    const bank = banks.find((b) => b.id === active);
    if (!window.confirm(`Reassign all payments/receipts that have no bank account to "${bank?.bank_name}"? This updates their ledger postings.`)) return;
    try {
      const { data } = await api.post(`/bank-accounts/${active}/assign-unassigned-payments`);
      toast.success(`${data.reassigned} payments reassigned to ${data.bank}`);
      loadBanks(); loadLines();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const manualMatch = async (lineId, paymentId) => {
    await api.post(`/bank/lines/${lineId}/match`, { payment_id: paymentId }); loadLines();
  };
  const unmatch = async (lineId) => { await api.post(`/bank/lines/${lineId}/unmatch`); loadLines(); };

  const matchedCount = lines.filter((l) => l.reconciliation_status === "Matched").length;

  return (
    <Layout>
      <PageHeader
        title="Bank Reconciliation"
        subtitle={active ? `${matchedCount}/${lines.length} lines matched` : "Add a bank account to begin"}
        actions={
          <>
            <Input type="month" data-testid="bank-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <span className="text-xs text-gray-500" data-testid="bank-coa-note">Bank &amp; cash accounts (name, currency, cash flag) are managed in the Chart of Accounts.</span>
          </>
        }
      />
      <div className="p-8">
        {banks.length === 0 ? (
          <div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">No bank accounts. Add one to upload statements and reconcile.</div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <SearchableSelect className="w-64" testid="bank-select" placeholder="Select bank account"
                value={active || ""} onValueChange={setActive}
                options={banks.map((b) => ({ value: b.id, label: `${b.raw_bank_name || b.bank_name} · ${b.currency}` }))} />
              {(() => { const ab = banks.find((b) => b.id === active); return ab ? (
                <div className="flex items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm" data-testid="bank-book-balance">
                  <span className="lx-label text-gray-400">Book Balance ({ab.gl_account_code})</span>
                  <span className={`lx-num font-semibold ${(ab.book_balance || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>TZS {fmtNum(ab.book_balance || 0)}</span>
                </div>
              ) : null; })()}
              {false && active && <Button onClick={openEditBank}><PencilSimple size={16} className="mr-1" />Edit account</Button>}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-gray-300 px-3 py-2 text-sm hover:border-[#1B4383]" data-testid="upload-statement">
                <UploadSimple size={16} /> Upload Statement (CSV)
                <input type="file" accept=".csv" className="hidden" onChange={(e) => { uploadCsv(e.target.files[0]); e.target.value = ""; }} />
              </label>
              <Button variant="outline" data-testid="download-template-btn" onClick={downloadTemplate}><DownloadSimple size={16} className="mr-1" />Download template</Button>
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="auto-match-btn" onClick={autoMatch}><MagicWand size={16} className="mr-1" />Auto-Match</Button>
              <Button variant="outline" data-testid="assign-unassigned-btn" onClick={assignUnassigned}><LinkSimple size={16} className="mr-1" />Assign unassigned payments here</Button>
            </div>

            {recon && (
              <div className="mb-4 rounded-sm border border-gray-200 bg-white" data-testid="recon-summary">
                <div className="border-b border-gray-100 px-4 py-2 lx-label text-gray-500">Reconciliation — {month}</div>
                <div className="grid grid-cols-2 gap-px bg-gray-100 sm:grid-cols-4">
                  <div className="bg-white px-4 py-3">
                    <div className="lx-label text-gray-400">GL Book Balance</div>
                    <div className={`lx-num text-lg font-semibold ${(recon.book_balance || 0) < 0 ? "text-red-600" : "text-gray-900"}`} data-testid="recon-book">TZS {fmtNum(recon.book_balance)}</div>
                  </div>
                  <div className="bg-white px-4 py-3">
                    <div className="lx-label text-gray-400">Statement Closing</div>
                    {recon.has_running_balance ? (
                      <div className="lx-num text-lg font-semibold text-gray-900" data-testid="recon-closing">{curSym(recon.currency)} {fmtNum(recon.statement_closing_balance)}</div>
                    ) : (
                      <div className="text-xs text-gray-400" data-testid="recon-closing-na">n/a — no running balance in CSV<br />(net movement {curSym(recon.currency)} {fmtNum(recon.net_movement)})</div>
                    )}
                  </div>
                  <div className="bg-white px-4 py-3">
                    <div className="lx-label text-gray-400">Variance (Book − Statement)</div>
                    {recon.variance === null || recon.variance === undefined ? (
                      <div className="text-xs text-gray-400" data-testid="recon-variance-na">Needs a statement closing balance</div>
                    ) : (
                      <div className={`lx-num text-lg font-semibold ${Math.abs(recon.variance) < 0.01 ? "text-emerald-700" : "text-red-600"}`} data-testid="recon-variance">
                        {curSym(recon.currency)} {fmtNum(recon.variance)} {Math.abs(recon.variance) < 0.01 ? "· Reconciled ✓" : ""}
                      </div>
                    )}
                  </div>
                  <div className="bg-white px-4 py-3">
                    <div className="lx-label text-gray-400">Open Reconciling Items</div>
                    <div className="text-xs text-gray-600" data-testid="recon-open-items">
                      {recon.unmatched_line_count} unmatched statement line(s)<br />
                      {recon.unreconciled_payment_count} unreconciled book payment(s)
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                  <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Payee</th>
                  <th className="px-3 py-2 text-left">Details</th><th className="px-3 py-2 text-right">Money In</th>
                  <th className="px-3 py-2 text-right">Money Out</th><th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Match</th>
                </tr></thead>
                <tbody>
                  {lines.length === 0 && <TableEmpty colSpan={7} title="No statement lines" message="Upload a CSV (columns: date, payee, details, credit/debit) to begin." />}
                  {lines.map((l) => (
                    <tr key={l.id} data-testid={`stmt-line-${l.id}`} className={`border-b border-gray-100 ${l.reconciliation_status === "Matched" ? "bg-emerald-50/40" : ""}`}>
                      <td className="px-3 py-2 lx-num">{fmtDate(l.date)}</td>
                      <td className="px-3 py-2">{l.payee || "—"}</td>
                      <td className="px-3 py-2 text-gray-500">{l.details || "—"}</td>
                      <td className="px-3 py-2 text-right lx-num text-emerald-700">{l.receipt_amount ? fmtNum(l.receipt_amount) : "—"}</td>
                      <td className="px-3 py-2 text-right lx-num text-red-600">{l.payment_amount ? fmtNum(l.payment_amount) : "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-sm px-1.5 py-0.5 text-xs ${l.reconciliation_status === "Matched" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{l.reconciliation_status}</span>
                      </td>
                      <td className="px-3 py-2">
                        {l.reconciliation_status === "Matched" ? (
                          <button className="text-xs text-gray-400 hover:text-red-500" data-testid={`unmatch-${l.id}`} onClick={() => unmatch(l.id)}>Unmatch</button>
                        ) : (
                          <SearchableSelect className="h-7 w-40 text-xs" testid={`match-select-${l.id}`} placeholder="Match payment…"
                            value="" onValueChange={(v) => manualMatch(l.id, v)}
                            options={payments.map((p) => ({ value: p.id, label: `${p.payee || p.direction} · ${fmtNum(p.amount)}` }))} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={newBankOpen} onOpenChange={(o) => { setNewBankOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="max-w-md" data-testid="bank-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Bank Account" : "New Bank Account"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label className="lx-label">Bank Name</Label><Input data-testid="bank-name" value={bankForm.bank_name || ""} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} className="mt-1" placeholder="CRDB / NMB / Stanbic" /></div>
            <div><Label className="lx-label">Currency</Label>
              <CurrencySelect value={bankForm.currency} onValueChange={(v) => setBankForm({ ...bankForm, currency: v })} testid="bank-currency" className="mt-1" />
            </div>
            <div><Label className="lx-label">Account No</Label><Input data-testid="bank-account-no" value={bankForm.account_number || ""} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2"><Label className="lx-label">Account Name</Label><Input data-testid="bank-account-name" value={bankForm.account_name || ""} onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })} className="mt-1" placeholder="Somcore Group" /></div>
            <div><Label className="lx-label">SWIFT Code</Label><Input data-testid="bank-swift" value={bankForm.swift_code || ""} onChange={(e) => setBankForm({ ...bankForm, swift_code: e.target.value })} className="mt-1" /></div>
            <div><Label className="lx-label">Branch</Label><Input data-testid="bank-branch" value={bankForm.branch || ""} onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2 flex items-center gap-2 rounded-sm border border-gray-200 px-3 py-2">
              <input type="checkbox" id="bank-is-cash" data-testid="bank-is-cash" disabled={!!editId} checked={!!bankForm.is_cash} onChange={(e) => setBankForm({ ...bankForm, is_cash: e.target.checked })} />
              <Label htmlFor="bank-is-cash" className="lx-label cursor-pointer">This is a CASH account — direct postings restricted to Finance &amp; Ronick{editId ? " (set at creation)" : ""}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewBankOpen(false); setEditId(null); }}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-bank" onClick={saveBank}>{editId ? "Save Changes" : "Add Account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
